/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  ReactNode,
  useCallback
} from 'react';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {
  BulkActionEntity,
  BulkActionType,
  CTCEntity
} from '@ion/core/src/types/entities';
import Alert from '@ion/dls/src/components/alert/Alert';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {useEditorStyles} from '@ion/dls/src/hooks/useEditorStyles';
import Render from '@ion/dls/src/render/Render';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {
  SelectedGridDataProps
} from '@ion/features/src/dialogs/types';
import useEntityLabel from '../hooks/useEntityLabel';
import ActionContainer from './ActionContainer';
import ActionList from './ActionList';
import {useStyles} from './Step';
import {BulkActions} from './types';

export interface ActionSelectionStepProps extends SelectedGridDataProps {
  entity: BulkActionEntity;
  actions: BulkActions;
  actionType: BulkActionType;
  form: ReactNode;
  onActionChange: (newAction: BulkActionType) => void;
  selectedRowEntities: CTCEntity[];
  taskNickName: string;
  selectedIdentifierType?: string;
  setTaskNickName: (newTaskNickName: string) => void;
  setTaskNickNameUserModified: (modified: boolean) => void;
  description: string;
  setDescription: (description: string) => void;
  hasCreateJob?: boolean;
  hasUpdateAsset?: boolean;
  hasCreateDevice?: boolean;
  disableServiceEnablement?: boolean;
  hasVBusAccess?: boolean;
  hasFilePermissions?: boolean;
  hasVehicleProfile?: boolean;
  hasDevicePassword?: boolean;
  disableScheduleDeviceSync?: boolean;
  entityIdentifiers?: Array<[]>;
}

export interface SelectionChip {
  id: number;
  name: string;
}

/**
 * This step will display the page containing the action list & a preview panel showing action detail forms.
 */
export const ActionSelectionStep = ({
  entity,
  selectedRowEntities,
  areTotalRowsSelected,
  entityIdentifiers,
  totalRowsCount,
  actions,
  form,
  actionType,
  onActionChange,
  taskNickName,
  selectedIdentifierType,
  setTaskNickName,
  setTaskNickNameUserModified,
  description,
  setDescription,
  hasVBusAccess = false,
  hasCreateJob = false,
  hasUpdateAsset = false,
  hasCreateDevice = false,
  disableScheduleDeviceSync = false,
  disableServiceEnablement = false,
  hasFilePermissions = false,
  hasVehicleProfile = false,
  hasDevicePassword = false
}: ActionSelectionStepProps) => {
  const localClasses                  = useStyles();
  const {classes}                     = useEditorStyles();

  const {singularEntityLabel, pluralEntityLabel} = useEntityLabel(entity);
  const {
    shared,
    bulkActions: {
      dialogBulkAction: {
        actionSelection
      }
    }
  } = translate('common:editors', {
    returnObjects : true,
    entity        : singularEntityLabel,
    entities      : pluralEntityLabel
  });

  const onTaskNickNameChange = useCallback(function({target}) {
    const nickName = target.value;
    setTaskNickName(nickName);
    setTaskNickNameUserModified(true);
  }, []);

  const onDescriptionChange = useCallback(function({target}) {
    const {value: description} = target;
    setDescription?.(description);
  }, []);

  return (
    <div className={classnames(classes.bulkFormWrapper, localClasses.step)}>
      <div className={localClasses.containerHeader}>
        <BodyTypography variant={'body2'} className={classes.body2}>
          {' '}
        </BodyTypography>
        <div className={localClasses.subtitle}>
          {shared.fieldsRequired}
        </div>
      </div>
      <div className={localClasses.actionContainer}>
        <ActionList actions={actions}
          disableServiceEnablement={disableServiceEnablement}
          disableScheduleDeviceSync={disableScheduleDeviceSync}
          hasCreateJob={hasCreateJob}
          hasUpdateAsset={hasUpdateAsset}
          hasCreateDevice={hasCreateDevice}
          selectedIdentifierType={selectedIdentifierType}
          onActionChange={onActionChange}
          selectedRowEntities={selectedRowEntities}
          areTotalRowsSelected={areTotalRowsSelected}
          entityIdentifiers={entityIdentifiers}
          totalRowsCount={totalRowsCount}
          hasFilePermissions={hasFilePermissions}
          hasVehicleProfile={hasVehicleProfile}
          hasDevicePassword={hasDevicePassword}
          hasVBusAccess={hasVBusAccess} />
        <ActionContainer form={form} />
      </div>
      <div className={localClasses.activityNameContainer}>
        <FormItem fullWidth disableMinWidth>
          <FormTextField required
            fieldLabel={translate('common:editors.bulkActions.activityName.label')}
            placeholder={translate('common:editors.bulkActions.activityName.placeholder')}
            name={'activityName'}
            onChange={onTaskNickNameChange}
            hasError={!taskNickName?.length}
            errorText={translate('common:editors.shared.required')}
            value={taskNickName}
            maxLength={1024} />
        </FormItem>
        <Render.If condition={actionType === BulkActionType.JOB_CREATION}>
          <FormItem fullWidth disableMinWidth>
            <FormTextField fieldLabel={translate('common:editors.labels.description')}
              placeholder={translate('common:editors.labels.description')}
              name={'description'}
              onChange={onDescriptionChange}
              value={description}
              maxLength={1024} />
          </FormItem>
        </Render.If>
      </div>
      <Render.If condition={actionType === BulkActionType.JOB_CREATION}>
        <Alert type='info'
          message={translate('jobcreate:errors.jobStatus')}
          gutters={false}
          constrained />
      </Render.If>
    </div>
  );
};

const isEntity = (item): boolean => !!item?._self;

export default ActionSelectionStep;
